import { ThemeEvents } from '@theme/events';

/**
 * @typedef {object} BookingCartItem
 * @property {number} product_id
 * @property {string} [handle]
 * @property {string} [url]
 */

class BookingButtonComponent extends HTMLElement {
  /** @type {HTMLAnchorElement|null} */
  #link = null;

  /** @type {AbortController|null} */
  #abort = null;

  /** @type {boolean} */
  #disabled = false;

  connectedCallback() {
    this.#link = this.querySelector('a');
    if (!this.#link) return;

    this.#abort = new AbortController();

    // Re-check enabled state when cart updates are emitted
    document.addEventListener(
      ThemeEvents.cartUpdate,
      () => {
        this.#updateEnabledState().catch(() => {});
      },
      { signal: this.#abort.signal },
    );

    // Initial enabled/disabled evaluation
    this.#updateEnabledState().catch(() => {});

    this.#link.addEventListener(
      'click',
      (e) => {
        this.#handleClick(e).catch(() => {});
      },
      { signal: this.#abort.signal },
    );
  }

  disconnectedCallback() {
    this.#abort?.abort();
    this.#abort = null;
  }

  get appointmentProductId() {
    const value = this.getAttribute('data-appointment-product-id');
    return value ? Number(value) : NaN;
  }

  get appointmentProductUrl() {
    return this.getAttribute('data-appointment-product-url') || this.#link?.getAttribute('href') || '/';
  }

  async #handleClick(event) {
    if (!this.#link) return;

    if (this.#disabled) {
      event.preventDefault();
      const message =
        this.getAttribute('data-disabled-message') ||
        'Booking is not available when your cart has more than 6 items.';
      if (typeof window !== 'undefined' && window.alert) window.alert(message);
      return;
    }

    const appointmentProductId = this.appointmentProductId;
    if (!Number.isFinite(appointmentProductId)) {
      // Without an ID we can't do cart logic; fall back to normal navigation.
      return;
    }

    event.preventDefault();

    const cart = await this.#fetchCart();
    const hasAppointment = cart.items.some((item) => item?.product_id === appointmentProductId);
    const hasOtherItems = this.#getBookingItems(cart.items).length > 0;

    if (!hasAppointment) {
      // No appointment yet — go to the appointment product page to pick a slot
      window.location.assign(this.appointmentProductUrl);
      return;
    }

    if (hasAppointment && hasOtherItems) {
      // Appointment + products selected — navigate to the confirmation/review form page
      const confirmationUrl = this.getAttribute('data-confirmation-url') || this.#link.href;
      window.location.assign(confirmationUrl);
      return;
    }

    // Has appointment but no other items — go to homepage to browse
    let redirectUrl = this.getAttribute('data-appointment-product-url');
    window.location.assign(redirectUrl);
  }

  /** Update the component enabled/disabled state and button label based on cart contents */
  async #updateEnabledState() {
    const cart = await this.#fetchCart();
    const items = Array.isArray(cart?.items) ? cart.items : [];
    const shouldDisable = this.#getBookingItems(items).length > 6;
    this.#setDisabled(shouldDisable);
    this.#updateButtonLabel(items);
  }

  /**
   * @param {BookingCartItem[]} items
   * @returns {BookingCartItem[]}
   */
  #getBookingItems(items) {
    const excludedHandles = new Set(
      (document.body.dataset.bookingExcludedProductHandles || '')
        .replace(/[\n\r]/g, ',')
        .split(',')
        .map((handle) => handle.trim().toLowerCase())
        .filter(Boolean),
    );
    const appointmentProductId = this.appointmentProductId;

    return items.filter((item) => {
      const handle = String(item?.handle ?? '').trim().toLowerCase();
      return (
        item?.product_id !== appointmentProductId &&
        !excludedHandles.has(handle) &&
        !String(item?.url ?? '').includes('sca_clone_freegift')
      );
    });
  }

  /**
   * Swap the link text based on whether there are eligible booking items in the cart.
   * @param {BookingCartItem[]} items
   */
  #updateButtonLabel(items) {
    if (!this.#link) return;
    const appointmentProductId = this.appointmentProductId;
    const labelFinalise = this.getAttribute('data-label-finalise') || '';
    const labelSelection = this.getAttribute('data-label-selection') || '';
    if (!labelFinalise && !labelSelection) return;

    const hasAppointment = items.some((item) => item?.product_id === appointmentProductId);
    const hasOtherItems = this.#getBookingItems(items).length > 0;

    this.#link.textContent = hasAppointment && hasOtherItems ? labelFinalise : labelSelection;
  }

  #setDisabled(disabled) {
    if (!this.#link) return;
    this.#disabled = Boolean(disabled);
    this.#link.classList.toggle('is-disabled', this.#disabled);
    if (this.#disabled) {
      this.#link.setAttribute('aria-disabled', 'true');
      this.#link.tabIndex = -1;
    } else {
      this.#link.removeAttribute('aria-disabled');
      this.#link.tabIndex = 0;
    }
  }

  async #fetchCart() {
    const res = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
    if (!res.ok) throw new Error(`Failed to fetch cart: ${res.status}`);
    return res.json();
  }
}

if (!customElements.get('booking-button-component')) {
  customElements.define('booking-button-component', BookingButtonComponent);
}
